import CameraService, { CameraPermissionState, CameraFacingMode } from '../../../helpers/classes/CameraService';
import EventService from '../../../helpers/classes/EventService';
import LanguageService from '../../../helpers/classes/LanguageService';
import RenderingService from '../../../helpers/classes/RenderingService';
import TemplateService from '../../../helpers/classes/TemplateService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import { HintType, ReticleType } from '../../enums/Camera';
import { ChangeMode, InputProvider } from '../../enums/InputProvider';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { Templates } from '../../enums/Templates';
import { TranslationKey } from '../../enums/Translation';
import { ImageProvider } from './image';
import { IMetaData } from '../../interfaces/MetaData';
import * as lottie from 'lottie-web/build/player/lottie';
import { FlowState } from '../../enums/FlowState';
import { ImageService } from '../../../helpers/classes/ImageService';
import { RequiredInputImage } from '../../enums/RequiredInputImage';
import DataService, { DataStoreType } from '../../classes/DataService';
import { IIdScanData } from '../../interfaces/DataFormats';
import { AnimationService } from '../../../helpers/classes/AnimationService';
import { TripleScanGuidanceProvider } from './triple-scan-guidance';
import HttpService from '../../../helpers/classes/HttpService';

export class CameraProvider {
	public static render(this: JourneyContainer, isFromTripleScanGuidancePage: boolean = false, isPredefinedJourney: boolean = false) {
		return TemplateService.getElements(this._container, {
			providerContainer: '[data-jcs-element="provider__container"]',
		})
			.then(({ providerContainer }) => TemplateService.renderToContainer(providerContainer, Templates.Camera))
			//.then((providerContainer) => this.changeCameraStream(undefined, ReticleType.DOCUMENT, false, HintType.NONE, false, CameraFacingMode.BACK).then(() => providerContainer))
			.then((providerContainer) => TemplateService.getElements(providerContainer, {
				cameraStatus: '[data-jcs-element="camera__status"]',
				cameraCapture: '[data-jcs-element="camera__capture"]',
				cameraViewfinder: '[data-jcs-element="camera__viewfinder"]',
				cameraSelect: '[data-jcs-element="camera__select"]',
				smartCaptureToggle: '[data-jcs-element="camera__manual__capture"]',
				smartCaptureToggleInner: '[data-jcs-element="camera__manual__capture__inner"]',
				modalSmartCaptureButton: '[data-jcs-element="capture__modal__camera__manual__capture"]',
				modalManualCaptureText: '[data-jcs-element="capture__modal__manual__capture__text"]',
				manualCaptureCheck: '[data-jcs-element="camera__manual__capture__check"]',
				modalManualCaptureCheck: '[data-jcs-element="capture__modal__camera__manual__capture__check"]',
				closeHelp: '[data-jcs-element="capture__close__help"]',
				openHelp: '[data-jcs-element="capture__open__help"]',
				helpDocumentContainer: '[data-jcs-element="capture__help__container"]',
				helpSelfieContainer: '[data-jcs-element="capture__selfie__help__container"]',
				helpAddressContainer: '[data-jcs-element="capture__address__help__container"]',
				helpCovidContainer: '[data-jcs-element="capture__covid__help__container"]',
				modalDocumentClose: '[data-jcs-element="capture__modal__close__help"]',
				modalSelfieClose: '[data-jcs-element="capture__selfie__modal__close__help"]',
				modalAddressClose: '[data-jcs-element="capture__address__modal__close__help"]',
				modalCovidClose: '[data-jcs-element="capture__covid__modal__close__help"]',
				modalDocumentGlareContainer: '[data-jcs-element="capture__modal__glare__animation__container"]',
				modalDocumentLowResContainer: '[data-jcs-element="capture__modal__low__res__animation__container"]',
				modalDocumentBlurContainer: '[data-jcs-element="capture__modal__blur__animation__container"]',
				modalAddressGlareContainer: '[data-jcs-element="capture__address__modal__glare__animation__container"]',
				modalAddressLowResContainer: '[data-jcs-element="capture__address__modal__low__res__animation__container"]',
				modalAddressBlurContainer: '[data-jcs-element="capture__address__modal__blur__animation__container"]',
				modalCovidGlareContainer: '[data-jcs-element="capture__covid__modal__glare__animation__container"]',
				modalCovidLowResContainer: '[data-jcs-element="capture__covid__modal__low__res__animation__container"]',
				modalCovidBlurContainer: '[data-jcs-element="capture__covid__modal__blur__animation__container"]',
				actionDocumentContainer: '[data-jcs-element="info__journey__action__container"]',
				actionText: '[data-jcs-element="info__journey__action__text"]',
				actionSelfieContainer: '[data-jcs-element="info__journey__action__selfie__container"]',
				actionAddressContainer: '[data-jcs-element="info__journey__action__address__container"]',
				actionCovidContainer: '[data-jcs-element="info__journey__action__covid__container"]',
				attemptCountContainer: '[data-jcs-element="attempt__count__container"]',
				attemptCounter: '[data-jcs-element="attempt__counter"]',
				loadingAnimationContainer: '[data-jcs-element="loading-animation"]'
			}))
			.then(({ cameraStatus, cameraCapture, cameraViewfinder, cameraSelect, smartCaptureToggle, smartCaptureToggleInner, modalSmartCaptureButton, modalManualCaptureText, manualCaptureCheck, modalManualCaptureCheck, closeHelp, openHelp, helpDocumentContainer, helpSelfieContainer, helpAddressContainer, modalDocumentClose, modalSelfieClose, modalAddressClose, modalDocumentGlareContainer, modalDocumentLowResContainer, modalDocumentBlurContainer, modalAddressGlareContainer, modalAddressLowResContainer, modalAddressBlurContainer, actionDocumentContainer, actionText, actionSelfieContainer, actionAddressContainer, attemptCountContainer, attemptCounter, loadingAnimationContainer, actionCovidContainer, helpCovidContainer, modalCovidBlurContainer, modalCovidClose, modalCovidGlareContainer, modalCovidLowResContainer }) => {
				RenderingService.hideObject(cameraSelect);
				RenderingService.setEnabled(cameraCapture as HTMLInputElement, false);
				RenderingService.setEnabled(cameraSelect as HTMLSelectElement, false);
				RenderingService.hideObject(closeHelp);

				CameraProvider.loadingAnimation = AnimationService.CreateAnimation(loadingAnimationContainer as HTMLDivElement, this.assetsFolder + '/animations/loading.json', true, true, {});
				if (this._journey) {
					// Check if triple scan guidance page mode is enabled & journey is on triple scan state
					if (this._journey.actionAttempt > 1 &&
						this._config.tripleScanGuidancePage && ((this._journey.action === FlowState.IDENTITY_BACK) || (this._journey.action === FlowState.IDENTITY_FRONT)) && !isFromTripleScanGuidancePage) {
						TripleScanGuidanceProvider.render.call(this, InputProvider.CAMERA);
					}
					else {
						isFromTripleScanGuidancePage = false;
						switch (this._journey.action) {
							case FlowState.SELFIE:
								RenderingService.showObject(actionSelfieContainer);
								RenderingService.hideObject(smartCaptureToggleInner);
								CameraProvider.reticle = ReticleType.PORTRAIT;
								CameraProvider.hint = HintType.NONE;
								CameraProvider.helpModal = helpSelfieContainer;
								CameraProvider.modalClose = modalSelfieClose;
								break;
							case FlowState.IDENTITY_BACK:
							case FlowState.IDENTITY_FRONT:
								RenderingService.showObject(actionDocumentContainer);
								RenderingService.showObject(smartCaptureToggleInner);
								CameraProvider.reticle = ReticleType.DOCUMENT;
								CameraProvider.hint = HintType.AUTOCAPTUREOFF;
								CameraProvider.helpModal = helpDocumentContainer;
								CameraProvider.modalClose = modalDocumentClose;
								CameraProvider.modalBlurContainer = modalDocumentBlurContainer;
								CameraProvider.modalGlareContainer = modalDocumentGlareContainer;
								CameraProvider.modalLowResContainer = modalDocumentLowResContainer;
								break;
							case FlowState.ADDRESS:
								RenderingService.showObject(actionAddressContainer);
								RenderingService.hideObject(smartCaptureToggleInner);
								CameraProvider.reticle = ReticleType.NONE;
								CameraProvider.hint = HintType.NONE;
								CameraProvider.helpModal = helpAddressContainer;
								CameraProvider.modalClose = modalAddressClose;
								CameraProvider.modalBlurContainer = modalAddressBlurContainer;
								CameraProvider.modalGlareContainer = modalAddressGlareContainer;
								CameraProvider.modalLowResContainer = modalAddressLowResContainer;
								break;
							case FlowState.COVIDPASS:
								RenderingService.showObject(actionCovidContainer);
								RenderingService.hideObject(smartCaptureToggleInner);
								CameraProvider.reticle = ReticleType.NONE;
								CameraProvider.hint = HintType.NONE;
								CameraProvider.helpModal = helpCovidContainer;
								CameraProvider.modalClose = modalCovidClose;
								CameraProvider.modalBlurContainer = modalCovidBlurContainer;
								CameraProvider.modalGlareContainer = modalCovidGlareContainer;
								CameraProvider.modalLowResContainer = modalCovidLowResContainer;
								break;
						}
					}
					if (this._config.hideAutoCaptureHints) {
						CameraProvider.hint = HintType.NONE;
					}
					switch (this._journey.actionAttempt) {
						case 2:
							RenderingService.setText(attemptCounter, '2');
							break;
						case 3:
							RenderingService.setText(attemptCounter, '1');
							break;
						default:
							RenderingService.hideObject(attemptCountContainer);
							break;
					}

					if (this._journey.action === FlowState.IDENTITY_FRONT) {
						RenderingService.setText(actionText, LanguageService.getTranslation(TranslationKey.CAMERA_MANUAL_CAPTURE_FRONT));
					}
					if (this._journey.action === FlowState.IDENTITY_BACK) {
						RenderingService.setText(actionText, LanguageService.getTranslation(TranslationKey.CAMERA_CAPTURE_BACK));
					}
				}
				return CameraService.getCameraPermission()
					.then((permission) => {
						switch (permission) {
							case CameraPermissionState.UNKNOWN: // safari 15.5 workaround
							case CameraPermissionState.GRANTED:
								RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.CAMERA_LOADING));
								break;

							default:
								break;
						}
					})
					.then(() => {
						let mirror: boolean | undefined;
						if (this._journey) {
							mirror = this._journey.action === FlowState.PASSIVE_LIVENESS || this._journey.action === FlowState.SELFIE;
						}
						if (smartCaptureToggle instanceof HTMLElement) {
							smartCaptureToggle.addEventListener('change', () => {

								this.changeInputProvider(InputProvider.SMART_CAPTURE, ChangeMode.DEFAULT);
								this.evaluate(true, isPredefinedJourney, InputProvider.SMART_CAPTURE);
							});
						}

						if (modalSmartCaptureButton instanceof HTMLElement) {
							modalSmartCaptureButton.addEventListener('change', () => {
								this.changeInputProvider(InputProvider.SMART_CAPTURE, ChangeMode.DEFAULT);
								this.evaluate(true, isPredefinedJourney, InputProvider.SMART_CAPTURE);
							});
						}

						if (openHelp instanceof HTMLElement) {
							openHelp.addEventListener('click', () => {
								RenderingService.showObject(CameraProvider.helpModal);
								RenderingService.showObject(closeHelp);
								RenderingService.hideObject(openHelp);
								if (CameraProvider.modalGlareContainer && CameraProvider.modalBlurContainer && CameraProvider.modalBlurContainer) {

									CameraProvider.glareAnimation = AnimationService.CreateAnimation(CameraProvider.modalGlareContainer as HTMLElement, this.assetsFolder + '/animations/glare.json', true, true, {});
									CameraProvider.blurAnimation = AnimationService.CreateAnimation(CameraProvider.modalBlurContainer as HTMLElement, this.assetsFolder + '/animations/blur.json', true, true, {});
									CameraProvider.lowResAnimation = AnimationService.CreateAnimation(CameraProvider.modalLowResContainer as HTMLElement, this.assetsFolder + '/animations/low-res.json', true, true, {});
									CameraProvider.glareAnimation.show();
									CameraProvider.glareAnimation.play();
									CameraProvider.blurAnimation.show();
									CameraProvider.blurAnimation.play();
									CameraProvider.lowResAnimation.show();
									CameraProvider.lowResAnimation.play();
								}
							});
						}

						if (closeHelp instanceof HTMLElement) {
							closeHelp.addEventListener('click', () => {
								CameraProvider.closeModal(CameraProvider.helpModal, closeHelp, openHelp);
							});
						}

						if (CameraProvider.modalClose instanceof HTMLElement) {
							CameraProvider.modalClose.addEventListener('click', () => {
								CameraProvider.closeModal(CameraProvider.helpModal, closeHelp, openHelp);
							});
						}

						if (cameraSelect instanceof HTMLSelectElement) {
							cameraSelect.addEventListener('change', () => {
								RenderingService.setText(cameraStatus, LanguageService.getTranslation(TranslationKey.CAMERA_LOADING));
								RenderingService.setEnabled(cameraSelect, false);
								RenderingService.setEnabled(cameraCapture as HTMLInputElement, false);

								this.changeCameraStream(cameraSelect.value, CameraProvider.reticle, !this._config.hideLogo, CameraProvider.hint, mirror)
									.then(() => this.renderAvailableCameras(cameraSelect as HTMLSelectElement))
									.then(() => {
										RenderingService.hideObject(cameraStatus);
										RenderingService.setEnabled(cameraSelect, true);
										RenderingService.setEnabled(cameraCapture as HTMLInputElement, true);
									})
									.catch((error) => {
										RenderingService.setEnabled(cameraSelect, true);
										RenderingService.showObject(cameraStatus);
										RenderingService.setText(cameraStatus, LanguageService.getTranslation(error.message as TranslationKey));
									});
							});

							RenderingService.setEnabled(cameraSelect as HTMLSelectElement, false);

							if (cameraCapture && cameraViewfinder instanceof HTMLCanvasElement) {
								cameraCapture.addEventListener('click', () => {
									const metaData: IMetaData[] = [{
										name: 'Capture Type',
										value: 'Manual Capture',
										step: '',
										type: 'Simple'
									}];

									RenderingService.setEnabled(cameraCapture as HTMLInputElement, false);
									RenderingService.showObject(loadingAnimationContainer);
									CameraService.getImageFromCurrentStream()
										.then((image) => {
											this.freezeCameraStream();
											return image;
										})
										.then((image) => CameraProvider.continueJourney.call(this, image, metaData))
										.then(() => this.updateJourneyFields());
								});

								let cameraFacingMode = CameraFacingMode.BACK;

								// select "Back Ultra Wide Camera" as default if exist
								CameraService.getVideoCameras().then((cameras) => {
									// Ensure this._journey is defined before accessing action
									if (this._journey?.action === FlowState.SELFIE) {
										// Set to front-facing camera and skip sending deviceId
										cameraFacingMode = CameraFacingMode.FRONT;
										return Promise.resolve(undefined); // No deviceId to send, return undefined
									}

									// If not SELFIE step, select back dual wide camera if available
									var backDualWideCamera = cameras.find(camera => CameraService.isBackDualWideCameraLabel(camera.label));
									let backDualWideCameraId = backDualWideCamera ? backDualWideCamera.deviceId : "";

									if (backDualWideCameraId !== "") {
										cameraFacingMode = CameraFacingMode.ENVIRONMENT;
										RenderingService.markSelected(cameraSelect, backDualWideCameraId);
										return backDualWideCameraId;
									}

									return Promise.resolve(undefined); // If no suitable camera is found, return undefined
								}).then((backDualWideCameraId) => {
									// If this._journey.action is SELFIE, backDualWideCameraId will be undefined
									return this.changeCameraStream(
										backDualWideCameraId,
										CameraProvider.reticle,
										!this._config.hideLogo,
										CameraProvider.hint,
										mirror,
										cameraFacingMode
									)
										.then(() => this.renderAvailableCameras(cameraSelect as HTMLSelectElement))
										.then(() => {
											RenderingService.hideObject(cameraStatus);
											RenderingService.setEnabled(cameraSelect as HTMLSelectElement, true);
											RenderingService.setEnabled(cameraCapture as HTMLInputElement, true);
											RenderingService.markSelected(cameraSelect, cameraSelect.value);
										})
										.catch(() => {
											return this.changeInputProvider(InputProvider.FILESYSTEM, ChangeMode.FORCE)
												.then(() => CameraService.stopStreaming())
												.then(() => this.evaluate());
										});
								})
							} else {
								return Promise.resolve();
							}
						}
					}, () => {
						console.log("Error");
					});

			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_PROVIDER, { provider: Templates.Camera }));
	}

	private static glareAnimation: lottie.AnimationItem;
	private static lowResAnimation: lottie.AnimationItem;
	private static blurAnimation: lottie.AnimationItem;
	private static loadingAnimation: lottie.AnimationItem;
	private static reticle: ReticleType;
	private static hint: HintType;
	private static helpModal: HTMLElement | undefined;
	private static modalClose: HTMLElement | undefined;
	private static modalGlareContainer: HTMLElement | undefined;
	private static modalBlurContainer: HTMLElement | undefined;
	private static modalLowResContainer: HTMLElement | undefined;

	private static continueJourney(this: JourneyContainer, image: string, metaData: IMetaData[]) {
		if (this._journey !== undefined) {
			switch (this._journey.action) {
				case FlowState.IDENTITY_BACK:
				case FlowState.IDENTITY_FRONT:
					if (this._previewPages.documentStep.manualCapture) {
						return ImageProvider.render.call(this, image, metaData);
					} else {
						return CameraProvider.continueJourneySkipPreview.call(this, image, metaData);
					}
				case FlowState.ADDRESS:
					if (this._previewPages.poaStep.manualCapture) {
						return ImageProvider.render.call(this, image, metaData);
					} else {
						return CameraProvider.continueJourneySkipPreview.call(this, image, metaData);
					}
				case FlowState.SELFIE:
					if (this._previewPages.facematchStep.manualCapture) {
						return ImageProvider.render.call(this, image, metaData);
					} else {
						return CameraProvider.continueJourneySkipPreview.call(this, image, metaData);
					}
				case FlowState.COVIDPASS:
					if (this._previewPages.covidStep.manualCapture) {

						return ImageProvider.render.call(this, image, metaData);
					} else {
						return CameraProvider.continueJourneySkipPreviewCovid.call(this, image, metaData);
					}
			}
		}
	}
	private static continueJourneySkipPreviewCovid(this: JourneyContainer, image: string, metaData: IMetaData[]) {

		return this.assertImageExists(image)
			.then(() => this.getJourney())
			.then((journey) => journey.continueCovid(
				image,
				this._journey!.id

			)
			).then((res) => DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN).update(ImageProvider.parseResponseCovidData(res)))
			.then(() => this.evaluate())
			.then(this.updateJourneyFields).then(() => EventService.publish(JourneyEvent.DISPLAY_STEP, { step: Templates.Result }));

	}

	private static continueJourneySkipPreview(this: JourneyContainer, image: string, metaData: IMetaData[]) {

		const { type } = ImageService.getBase64Components(image);
		return this.assertImageExists(image)
			.then(() => this.getJourney())
			.then((journey) => journey.continue({
				imgList: [{
					...ImageService.remapNames(ImageService.getBase64Components(image)),
					name: journey.action === 'SELFIE' ? RequiredInputImage.SelfiePhoto : RequiredInputImage.WhiteImage,
					inputFormat: type === 'pdf' ? 1 : 0
				}],
				journeyDefinitionId: this._journeyDefinitionId
			}, metaData)
				.then((res) => DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN).update(ImageProvider.parseResponseData(res)))
				.then(() => this.evaluate())
				.then(this.updateJourneyFields));
	}

	private static closeModal(helpContainer: HTMLElement | undefined, closeHelp: HTMLElement | undefined, openHelp: HTMLElement | undefined) {
		RenderingService.hideObject(helpContainer);
		RenderingService.hideObject(closeHelp);
		RenderingService.showObject(openHelp);
		CameraProvider.glareAnimation.stop();
		CameraProvider.glareAnimation.destroy();
		CameraProvider.blurAnimation.stop();
		CameraProvider.blurAnimation.destroy();
		CameraProvider.lowResAnimation.stop();
		CameraProvider.lowResAnimation.destroy();
	}
}
